import { AnalyticalEvent, AnalyticalEventName } from '../type/analytics.type';
import { EVENT_CACHE_LOCAL_STORAGE_KEY } from '../type/storage.type';

import { logDebug } from './logDebug.util';

let eventTracker: EventTracker;

type TrackerOptions = {
    apiUrl: string;
    workspaceId: string;
    flowId: string;
    isPreviewMode: boolean;
    source: string;
    visitorId: string | null;
};

type EventTracker = {
    track: (
        name: AnalyticalEventName,
        options?: Record<string, string | number>,
    ) => void;
    trackLatest: (
        name: AnalyticalEventName,
        options?: Record<string, string | number>,
    ) => void;
    reset: () => void;
    submit: () => void;
    setOptions: (options: TrackerOptions) => void;
};

function buildEventTracker(trackerOptions: TrackerOptions): EventTracker {
    let pendingEventList = getCachedPendingEvents();
    var submitTaskTimerId: NodeJS.Timeout | null = null;

    var BATCH_DELAY = 5 * 1000;

    function submit() {
        if (!trackerOptions.workspaceId) {
            logDebug(
                `[ReelFlow][buildEventTracker] Required options are missing. Skipping events submission`,
                { trackerOptions, pendingEventList },
            );

            return;
        }

        logDebug(`[ReelFlow][buildEventTracker] Submitting pending events`, {
            pendingEventList,
        });

        var baseUrl = trackerOptions.apiUrl;
        var url = baseUrl + 'analytics/events';

        var requestBody = {
            workspace: trackerOptions.flowId
                ? `${trackerOptions.workspaceId}-${trackerOptions.flowId}`
                : `${trackerOptions.workspaceId}`,
            events: pendingEventList,
        };

        var requestHeaders = {
            type: 'application/json',
        };

        var rawBody = new Blob([JSON.stringify(requestBody)], requestHeaders);

        var LOCAL_DOMAINS = [
            'localhost',
            '127.0.0.1',
            '',
            '::1',
            'reelflow.test',
            'reelflow-api.test',
        ];

        var isLocalhost =
            window.location &&
            window.location.hostname &&
            LOCAL_DOMAINS.includes(window.location.hostname);

        // var isLocalhost = false;

        var isPreviewMode = trackerOptions.isPreviewMode;

        var isQueued = isLocalhost || isPreviewMode || false;

        if (!isLocalhost && !isPreviewMode) {
            isQueued = navigator.sendBeacon(url, rawBody);
        }

        if (isQueued) {
            pendingEventList = [];

            cachePendingEvents();
        }
    }

    function cachePendingEvents() {
        try {
            let rawCachedPendingEvents = localStorage.getItem(
                EVENT_CACHE_LOCAL_STORAGE_KEY,
            );

            if (!rawCachedPendingEvents) {
                rawCachedPendingEvents = '{}';
            }

            var cachedPendingEvents = JSON.parse(rawCachedPendingEvents);

            cachedPendingEvents[trackerOptions.workspaceId] = pendingEventList;

            localStorage.setItem(
                EVENT_CACHE_LOCAL_STORAGE_KEY,
                JSON.stringify(cachedPendingEvents),
            );
        } catch (ex) {}
    }

    function getCachedPendingEvents() {
        try {
            const rawCachedPendingEvents = localStorage.getItem(
                EVENT_CACHE_LOCAL_STORAGE_KEY,
            );

            if (!rawCachedPendingEvents) {
                return [] as AnalyticalEvent[];
            }

            var cachedPendingEvents = JSON.parse(rawCachedPendingEvents);

            // TODO: Validate cached pending events
            if (
                !cachedPendingEvents ||
                !cachedPendingEvents[trackerOptions.workspaceId] ||
                !Array.isArray(cachedPendingEvents[trackerOptions.workspaceId])
            ) {
                return [] as AnalyticalEvent[];
            }

            return cachedPendingEvents[
                trackerOptions.workspaceId
            ] as AnalyticalEvent[];
        } catch (ex) {}

        return [] as AnalyticalEvent[];
    }

    function track(
        name: AnalyticalEventName,
        payload?: Record<string, string | number>,
    ) {
        var payloadAttrBlacklist = ['_payloadVersion'];
        var eventPayload = {};

        Object.keys(payload || {}).forEach(function (key) {
            if (payloadAttrBlacklist.includes(key)) {
                return;
            }

            // @ts-expect-error
            eventPayload[key] = payload[key];
        });

        logDebug(`[ReelFlow][buildEventTracker][track] Track event`, {
            name,
            payload: eventPayload,
        });

        let eventData: AnalyticalEvent = {
            name,
            payload: eventPayload,
            payload_version:
                payload && payload._payloadVersion
                    ? (payload._payloadVersion as number)
                    : 1,
            timestamp: Date.now(),
            source: trackerOptions.source,
        };

        if (trackerOptions.visitorId) {
            eventData.visitor_uuid = trackerOptions.visitorId;
        }

        pendingEventList.push(eventData);

        cachePendingEvents();

        if (submitTaskTimerId) {
            return;
        }

        submitTaskTimerId = setTimeout(() => {
            submitTaskTimerId = null;

            submit();
        }, BATCH_DELAY);
    }

    return {
        track: track,
        trackLatest: (
            name: AnalyticalEventName,
            payload?: Record<string, string | number>,
        ) => {
            // NOTE: Remove previous events from the queue
            pendingEventList = pendingEventList.filter(
                (event) => event.name !== name,
            );

            track(name, payload);
        },
        reset: () => {
            logDebug(
                `[ReelFlow][buildEventTracker][reset] Resetting event tracker`,
            );

            pendingEventList = [];

            if (submitTaskTimerId !== null) {
                clearTimeout(submitTaskTimerId);

                submitTaskTimerId = null;
            }
        },
        // Submits all pending events right away
        submit: () => {
            if (pendingEventList.length === 0) {
                logDebug(
                    `[ReelFlow][buildEventTracker][submit] No pending events to submit`,
                );

                return;
            }

            if (submitTaskTimerId !== null) {
                logDebug(
                    `[ReelFlow][buildEventTracker][submit] Clearing scheduled task`,
                );

                clearTimeout(submitTaskTimerId);

                submitTaskTimerId = null;
            }

            submit();
        },
        setOptions: (options: TrackerOptions) => {
            Object.keys(options).forEach((optionKey) => {
                // @ts-expect-error
                trackerOptions[optionKey] = options[optionKey];
            });
        },
    };
}

export function initializeEventTracker(options: TrackerOptions) {
    eventTracker = buildEventTracker(options);
}

export function getEventTracker() {
    return eventTracker;
}
